import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import PresenceActiveIcon from "@atlaskit/icon/glyph/presence-active";
import { media } from "@atlaskit/primitives/responsive";

import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { Link } from "@/libraries/design/components";

import { tabbedHeroSectionPagesMessages } from "../messages/tabbed-hero-section.messages";

import { ContentTypeVideo } from "./content-type-video";

import type { TabProps, TabType } from "./tab-type";

const hideMobile = css({
    [media.below.md]: {
        display: "none"
    }
});

type PagesContentTypeProps = TabProps & { isConfluenceCrossflow?: boolean };

export const PagesContentType: TabType = ({
    index,
    isConfluenceCrossflow
}: PagesContentTypeProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={intl.formatMessage(
                tabbedHeroSectionPagesMessages.contentTitle
            )}
            description={intl.formatMessage(
                tabbedHeroSectionPagesMessages.contentDescription
            )}
            iconTitle={intl.formatMessage(
                tabbedHeroSectionPagesMessages.contentIconTitle
            )}
            iconCollection={[
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionPagesMessages.contentFeaturePoint1
                    ),
                    icon: <PresenceActiveIcon size="small" label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionPagesMessages.contentFeaturePoint2
                    ),
                    icon: <PresenceActiveIcon size="small" label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionPagesMessages.contentFeaturePoint3
                    ),
                    icon: <PresenceActiveIcon size="small" label="" />
                }
            ]}
            cta={
                intl.locale === "en" ? (
                    <div css={hideMobile}>
                        <Link
                            href={
                                "https://www.atlassian.com/confluence-sandbox-editor"
                            }
                            text={intl.formatMessage(
                                tabbedHeroSectionPagesMessages.contentCta
                            )}
                            type={"primary"}
                            dataEventProps={
                                !isConfluenceCrossflow
                                    ? {
                                          "data-product-key": "confluence",
                                          "data-event-component": "linkButton",
                                          "data-event-container":
                                              "contentTypesSection",
                                          "data-event": "clicked",
                                          "data-label": "try-it-out",
                                          "data-label-english": "Try it out",
                                          "data-track-event":
                                              "confluence-pt.content-types.pages.cta"
                                      }
                                    : undefined
                            }
                            onClick={() => {
                                if (!isConfluenceCrossflow) {
                                    return;
                                }
                                createAnalyticsEvent({
                                    eventName:
                                        "confluence-crossflow-tab-cta-clicked",
                                    eventType: "ui",
                                    action: "clicked",
                                    actionSubject: "linkButton",
                                    actionSubjectId:
                                        "confluenceCrossFlowHeroSection",
                                    attributes: {
                                        reactComponent: "PagesContentType",
                                        tab: "pages",
                                        userTeamType:
                                            user.extended_profile?.team_type
                                    }
                                }).fire();
                            }}
                        />
                    </div>
                ) : undefined
            }
            reverseLayout
            videoSrcMp4="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Pages_LowBR_reencode.mp4"
            videoSrcWebm="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Pages_LowBR.webm"
            videoPreviewSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Pages_Preview.webp"
        />
    );
};
